import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {UNASSIGNED_TASK_TEXT} from 'processes/ProcessConstants';
import {getStore} from 'redux-stores/Store';
import getTestProps from 'test/lib/cypress/getTestProps';
import Avatar, {HasAvatar, NO_AVATAR_URL} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import AvatarIcon from 'ui-library/icons/AvatarIcon';

/**
 * Renders an avatar for the assignee, if it's a valid assignee.
 * If the task is unassigned, we have a special design.
 * Otherwise, returns null; caller can do what they like.
 */
export function renderProcessTaskAssigneeAvatar(typedValue: TypedValue): JSX.Element | null {
  if (typedValue.type === BaseTypedValueType.USER) {
    const user = getStore().getState().models.managed.entitySummariesByHandle?.[typedValue.userHandle] ?? {
      avatar_url: typedValue.avatarUrl,
      handle: typedValue.userHandle,
      name: typedValue.name ?? typedValue.userHandle,
    };
    return <AssigneeAvatar party={user} />;
  } else if (isTypedValueEmpty(typedValue)) {
    const unassignedEntity = {
      avatar_url: NO_AVATAR_URL,
      name: UNASSIGNED_TASK_TEXT,
    };
    return <AssigneeAvatar party={unassignedEntity} useNoAvatar={true} />;
  } else {
    return null;
  }
}

interface AssigneeAvatarProps {
  party: HasAvatar & {name: string};
  useNoAvatar?: boolean;
}

export function AssigneeAvatar({party, useNoAvatar}: AssigneeAvatarProps): JSX.Element {
  return (
    <span className={css(styles.assigneeCell)}>
      {useNoAvatar ? (
        <AvatarIcon avatarUrl={NO_AVATAR_URL} size={AvatarSize.XSMALL} />
      ) : (
        <Avatar entity={party} size={AvatarSize.XSMALL} />
      )}
      <span
        className={classNames('u-tiny-spacing-left', css(styles.assigneeName))}
        {...getTestProps({
          component: 'AssigneeAvatar',
          element: 'text',
        })}
      >
        {party.name}
      </span>
    </span>
  );
}

const styles = StyleSheet.create({
  assigneeCell: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  assigneeName: {
    width: 'calc(100% - 25px)',
  },
});
